import * as React from 'react';

import {
  Badge,
  Button,
  ConfirmPopoverButton,
  DialogClosingConfirmation,
  EntityDetails,
  LocalizedDate,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { ArrowLeft, Trash2 } from 'lucide-react';

import { itemGroupDeleteAtom } from '../../../_graphql/mutationItemGroupDelete';
import { itemGroupUpdateAtom } from '../../../_graphql/mutationItemGroupUpdate';
import * as state from '../../../_state/itemGroupDetail.state';
import {
  $hasItemGroupUnsavedChanges,
  $itemGroupDiscardBaseline,
  $resetItemGroupFormToBaseline,
  ItemGroupFormState,
} from '../../../_state/itemGroupForm.state';
import { useSettingsLocation } from '../../../_state/settingsModule.state';
import { usePopulateItemGroupForm } from '../../../hooks/usePopulateItemGroupForm';
import { useTranslations } from '../../../i18n/useTranslations';

import { ItemGroupTabDetails } from './ItemGroupTabDetails';

type ItemGroupDetailTab = 'details';

export const ItemGroupDetailView = () => {
  const t = useTranslations();
  const [, setLocation] = useSettingsLocation();

  const [currentTab, setCurrentTab] =
    React.useState<ItemGroupDetailTab>('details');
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [isUpdating, setIsUpdating] = React.useState(false);
  const [pendingTabChange, setPendingTabChange] =
    React.useState<ItemGroupDetailTab | null>(null);

  const itemGroupId = useAtomValue(state.$itemGroupId);
  const {
    data: itemGroup,
    isFetching,
    refetch,
  } = useAtomValue(state.$remoteItemGroupDetail);
  const isLoading = useAtomValue(state.$isItemGroupLoading);
  const error = useAtomValue(state.$itemGroupError);
  const hasUnsavedChanges = useAtomValue($hasItemGroupUnsavedChanges);

  const [, resetForm] = useAtom($resetItemGroupFormToBaseline);
  const [discardBaseline, setDiscardBaseline] = useAtom(
    $itemGroupDiscardBaseline,
  );

  const deleteItemGroup = useSetAtom(itemGroupDeleteAtom({}));
  const updateItemGroup = useSetAtom(itemGroupUpdateAtom({}));

  const toast = useToast();
  usePopulateItemGroupForm({ itemGroup });

  const handleBack = React.useCallback(() => {
    setLocation('/inventory');
  }, [setLocation]);

  const handleDiscard = React.useCallback(() => {
    resetForm();
    toast.success(t.inventoryView.itemGroupDetailView.toast.discarded);
  }, [resetForm, toast, t]);

  const handleSaveUpdates = React.useCallback(
    async (data?: ItemGroupFormState) => {
      if (!itemGroupId || !data) return;

      if (!data.name.trim()) {
        toast.error(
          t.inventoryView.itemGroupCreateDialog.validation.nameRequired,
        );
        return;
      }
      if (!data.number.trim()) {
        toast.error(
          t.inventoryView.itemGroupCreateDialog.validation.numberRequired,
        );
        return;
      }

      try {
        setIsUpdating(true);
        await updateItemGroup({
          id: itemGroupId,
          name: data.name.trim(),
          number: data.number.trim(),
          parentId: data.parentId ?? null,
          description: data.description.trim() || null,
          notes: data.notes.trim() || null,
          defaultPricingUnit: data.defaultPricingUnit ?? null,
        });

        setDiscardBaseline(data);
        toast.success(t.inventoryView.itemGroupDetailView.toast.updated);
        refetch?.();
      } catch (err) {
        toast.error(
          t.inventoryView.itemGroupDetailView.toast.updateError(
            err instanceof Error ? err.message : 'Unknown error',
          ),
        );
      } finally {
        setIsUpdating(false);
      }
    },
    [itemGroupId, updateItemGroup, setDiscardBaseline, toast, t, refetch],
  );

  const handleBeforeChangeTab = React.useCallback(
    (newTab: string) => {
      if (hasUnsavedChanges) {
        setPendingTabChange(newTab as ItemGroupDetailTab);
        return false;
      }
      return true;
    },
    [hasUnsavedChanges],
  );

  const handleConfirmTabChange = React.useCallback(() => {
    if (pendingTabChange && discardBaseline) {
      resetForm();
      setCurrentTab(pendingTabChange);
    }
    setPendingTabChange(null);
  }, [pendingTabChange, discardBaseline, resetForm]);

  const handleCancelTabChange = React.useCallback(() => {
    setPendingTabChange(null);
  }, []);

  const handleDelete = React.useCallback(async () => {
    if (!itemGroupId) return;

    try {
      setIsDeleting(true);
      await deleteItemGroup({ id: itemGroupId });
      toast.success(t.inventoryView.itemGroupDetailView.messages.deleteSuccess);
      setLocation('/inventory');
    } catch (err) {
      toast.error(
        t.inventoryView.itemGroupDetailView.messages.deleteError(
          err instanceof Error ? err.message : 'Unknown error',
        ),
      );
    } finally {
      setIsDeleting(false);
    }
  }, [itemGroupId, deleteItemGroup, toast, setLocation, t]);

  const tools = React.useMemo(() => {
    if (!itemGroup) return null;
    return (
      <ConfirmPopoverButton
        onConfirm={handleDelete}
        message={
          t.inventoryView.itemGroupDetailView.messages.deleteConfirmation
        }
        confirmLabel={t.inventoryView.common.confirmDelete}
        cancelLabel={t.inventoryView.common.cancel}
        buttonProps={{
          variant: 'outline',
          color: 'failure',
          icon: <Trash2 className="size-4" />,
          isDisabled: isDeleting,
        }}
      >
        {isDeleting
          ? t.inventoryView.itemGroupDetailView.actions.deleting
          : t.inventoryView.itemGroupDetailView.actions.delete}
      </ConfirmPopoverButton>
    );
  }, [itemGroup, handleDelete, isDeleting, t]);

  if (isLoading || isFetching) {
    return (
      <div className="@ItemGroupDetailView flex h-full flex-col">
        <div className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 w-1/4 rounded bg-neutral/20"></div>
            <div className="h-8 w-1/3 rounded bg-neutral/20"></div>
            <div className="h-32 rounded bg-neutral/20"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !itemGroup) {
    return (
      <div className="@ItemGroupDetailView flex h-full flex-col p-6">
        <div className="text-failure">
          Error:{' '}
          {error?.message ??
            t.inventoryView.itemGroupDetailView.messages.itemGroupNotFound}
        </div>
        <Button variant="outline" onPress={handleBack} className="mt-4 w-fit">
          <ArrowLeft className="mr-2 size-4" />
          {t.inventoryView.itemGroupDetailView.actions.backToItemGroups}
        </Button>
      </div>
    );
  }

  return (
    <div className="@ItemGroupDetailView flex h-full flex-col">
      <EntityDetails
        className="relative"
        onChangeTab={(tab) => setCurrentTab(tab as ItemGroupDetailTab)}
        onBeforeChangeTab={handleBeforeChangeTab}
        currentTab={currentTab}
        onBackPressed={handleBack}
        primaryTitle={itemGroup.name}
        subtitle={
          <>
            {t.inventoryView.itemGroupDetailView.fields.number}:{' '}
            <span className="font-mono">{itemGroup.number}</span>
            <span className="ml-2 text-neutral/60">
              • {t.inventoryView.itemGroupDetailView.fields.created}{' '}
              <LocalizedDate date={itemGroup.timestamps.createdAt} />
            </span>
          </>
        }
        badges={
          itemGroup.parent && (
            <Badge color="neutral" size="small">
              {t.inventoryView.itemGroupDetailView.fields.parentGroup}:{' '}
              {itemGroup.parent.name}
            </Badge>
          )
        }
        headerActions={tools}
        tabs={{
          details: {
            title: t.inventoryView.itemGroupDetailView.tabs.details,
            content: () => (
              <ItemGroupTabDetails
                itemGroup={itemGroup}
                onDiscard={handleDiscard}
                onSaveUpdates={handleSaveUpdates}
                isSaving={isUpdating}
              />
            ),
          },
        }}
      />
      <DialogClosingConfirmation
        isOpen={!!pendingTabChange}
        message={
          t.inventoryView.itemGroupDetailView.messages.unsavedDataMessage
        }
        onCancel={handleCancelTabChange}
        onDiscard={handleConfirmTabChange}
      />
    </div>
  );
};
